import React, { useState, useRef, useEffect } from 'react';
import {
  Input,
  Box,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Button,
  Flex,
  Text,
  IconButton,
  Grid,
  VStack,
} from '@chakra-ui/react';
import moment from 'moment';

const GregorianDatePicker = ({ value, onChange, placeholder = 'اختر التاريخ', ...props }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [displayValue, setDisplayValue] = useState('');
  const initialRef = useRef();

  useEffect(() => {
    if (value) {
      try {
        const gregorianDate = moment(value, 'YYYY-MM-DD');
        const formatted = gregorianDate.format('DD MMMM YYYY');
        setDisplayValue(formatted + ' م');
      } catch (e) {
        setDisplayValue(value);
      }
    } else {
      setDisplayValue('');
    }
  }, [value]);

  const handleDateSelect = (year, month, day) => {
    try {
      // Format month and day with leading zeros if needed
      const monthStr = month < 10 ? `0${month}` : `${month}`;
      const dayStr = day < 10 ? `0${day}` : `${day}`;
      const dateString = `${year}-${monthStr}-${dayStr}`;
      const gregorianDate = moment(dateString, 'YYYY-MM-DD');
      const formatted = gregorianDate.format('YYYY-MM-DD');
      const display = gregorianDate.format('DD MMMM YYYY');
      
      setDisplayValue(display + ' م');
      onChange(formatted);
      setIsOpen(false);
    } catch (e) {
      console.error('Error selecting Gregorian date:', e);
    }
  };

  const getCurrentGregorianDate = () => {
    const now = moment();
    return {
      year: parseInt(now.format('YYYY')),
      month: parseInt(now.format('M')),
      day: parseInt(now.format('D')),
    };
  };

  const getCalendarDays = (year, month) => {
    // Format month with leading zero if needed
    const monthStr = month < 10 ? `0${month}` : `${month}`;
    const gregorianDate = moment(`${year}-${monthStr}`, 'YYYY-MM');
    const daysInMonth = gregorianDate.daysInMonth();
    const days = [];
    
    // Get the first day of the month
    const firstDay = moment(`${year}-${monthStr}-01`, 'YYYY-MM-DD');
    const dayOfWeek = firstDay.day(); // 0 = Sunday, 6 = Saturday
    
    // Adjust for Arabic week (Saturday = 0, Friday = 6)
    const arabicDayOfWeek = (dayOfWeek + 1) % 7;
    
    // Add empty cells for days before the first day
    for (let i = 0; i < arabicDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }
    
    return days;
  };

  const CalendarContent = () => {
    const current = getCurrentGregorianDate();
    const [selectedYear, setSelectedYear] = useState(current.year);
    const [selectedMonth, setSelectedMonth] = useState(current.month);
    
    const gregorianMonths = [
      'يناير', 'فبراير', 'مارس', 'أبريل',
      'مايو', 'يونيو', 'يوليو', 'أغسطس',
      'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'
    ];
    
    const days = getCalendarDays(selectedYear, selectedMonth);
    const weekDays = ['س', 'ح', 'ن', 'ث', 'ر', 'خ', 'ج'];
    
    const handlePrevMonth = () => {
      if (selectedMonth === 1) {
        setSelectedMonth(12);
        setSelectedYear(selectedYear - 1);
      } else {
        setSelectedMonth(selectedMonth - 1);
      }
    };
    
    const handleNextMonth = () => {
      if (selectedMonth === 12) {
        setSelectedMonth(1);
        setSelectedYear(selectedYear + 1);
      } else {
        setSelectedMonth(selectedMonth + 1);
      }
    };
    
    const isToday = (day) => {
      if (!day) return false;
      const today = getCurrentGregorianDate();
      return (
        day === today.day &&
        selectedMonth === today.month &&
        selectedYear === today.year
      );
    };
    
    return (
      <Box p={4} minW="320px" dir="rtl">
        <Flex justify="space-between" align="center" mb={4}>
          <IconButton
            aria-label="الشهر السابق"
            onClick={handlePrevMonth}
            size="sm"
            icon={<Text fontSize="lg">‹</Text>}
          />
          <VStack spacing={0}>
            <Text fontWeight="bold" fontSize="lg">
              {gregorianMonths[selectedMonth - 1]}
            </Text>
            <Text fontSize="sm" color="gray.600">
              {selectedYear} م
            </Text>
          </VStack>
          <IconButton
            aria-label="الشهر التالي"
            onClick={handleNextMonth}
            size="sm"
            icon={<Text fontSize="lg">›</Text>}
          />
        </Flex>
        
        <Grid templateColumns="repeat(7, 1fr)" gap={1} mb={2}>
          {weekDays.map((day, idx) => (
            <Box
              key={idx}
              textAlign="center"
              fontSize="xs"
              fontWeight="bold"
              color="gray.600"
              py={1}
            >
              {day}
            </Box>
          ))}
        </Grid>
        
        <Grid templateColumns="repeat(7, 1fr)" gap={1}>
          {days.map((day, idx) => {
            if (day === null) {
              return <Box key={`empty-${idx}`} />;
            }
            return (
              <Button
                key={`day-${day}`}
                size="sm"
                variant={isToday(day) ? 'solid' : 'ghost'}
                colorScheme={isToday(day) ? 'primary' : 'gray'}
                onClick={() => handleDateSelect(selectedYear, selectedMonth, day)}
                _hover={{ bg: 'primary.100' }}
                minH="32px"
              >
                {day}
              </Button>
            );
          })}
        </Grid>
        
        <Flex justify="center" mt={4}>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              const today = getCurrentGregorianDate();
              handleDateSelect(today.year, today.month, today.day);
            }}
          >
            اليوم
          </Button>
        </Flex>
      </Box>
    );
  };

  return (
    <Popover
      isOpen={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      initialFocusRef={initialRef}
      placement="bottom-start"
    >
      <PopoverTrigger>
        <Input
          {...props}
          ref={initialRef}
          value={displayValue}
          placeholder={placeholder}
          readOnly
          cursor="pointer"
          onClick={() => setIsOpen(true)}
        />
      </PopoverTrigger>
      <PopoverContent>
        <PopoverBody p={0}>
          <CalendarContent />
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

export default GregorianDatePicker;

